import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { api, getUser } from "../lib/api";
import { CheckCircle2, XCircle, Loader2, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// Poll for up to ~60 seconds. Stripe can take a while to flip a Checkout Session
// from "unpaid" to "paid" after a redirect — especially for card networks that
// go through 3DS or after the app returns from a background Safari tab.
const POLL_DELAYS_MS = [1500, 1500, 2000, 2000, 3000, 3000, 4000, 4000, 5000, 5000, 6000, 6000, 8000, 8000];

function openBinshareApp(sessionId) {
  // Custom URL scheme handoff — works when the Capacitor app is installed.
  // If the scheme isn't registered (web-only user), nothing happens and the
  // user stays on this page, which is the correct fallback.
  const url = sessionId
    ? `binshare://payment-result?session_id=${encodeURIComponent(sessionId)}`
    : "binshare://payment-result";
  window.location.href = url;
}

export default function PaymentResult({ outcome }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const user = getUser();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ status: "checking", payment_status: "" });
  const [attempts, setAttempts] = useState(0);
  const [phase, setPhase] = useState("polling"); // polling | paid | timeout | manual_checking
  const [photoSaved] = useState(false);
  const cancelledRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    if (!sessionId) return null;
    try {
      const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
      setStatus(data);
      return data;
    } catch {
      return null;
    }
  }, [sessionId]);

  useEffect(() => {
    if (outcome !== "success" || !sessionId) return;
    cancelledRef.current = false;
    const runPoll = async () => {
      for (let i = 0; i < POLL_DELAYS_MS.length; i++) {
        if (cancelledRef.current) return;
        setAttempts(i);
        const data = await fetchStatus();
        if (cancelledRef.current) return;
        if (data && (data.payment_status === "paid" || data.status === "expired")) {
          setPhase(data.payment_status === "paid" ? "paid" : "timeout");
          return;
        }
        await new Promise((r) => setTimeout(r, POLL_DELAYS_MS[i]));
      }
      if (!cancelledRef.current) setPhase("timeout");
    };
    runPoll();
    return () => {
      cancelledRef.current = true;
    };
  }, [sessionId, outcome, fetchStatus]);

  const manualCheck = async () => {
    setPhase("manual_checking");
    const data = await fetchStatus();
    if (data && data.payment_status === "paid") {
      setPhase("paid");
    } else {
      setPhase("timeout");
    }
  };

  if (outcome === "cancel") {
    return (
      <Layout>
        <div className="max-w-xl mx-auto px-5 py-20 text-center">
          <XCircle className="w-16 h-16 text-[#D67B54] mx-auto" />
          <h1 className="mt-4 text-3xl tracking-tight font-medium" style={{ fontFamily: "Outfit" }}>Payment cancelled</h1>
          <p className="mt-2 text-[#6B6965]">No worries — you can try again any time.</p>
          <Button data-testid="back-dashboard" onClick={() => navigate("/dashboard")} className="mt-6 rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white">
            Back to dashboard
          </Button>
        </div>
      </Layout>
    );
  }

  const paid = phase === "paid";
  const timedOut = phase === "timeout";
  const checking = phase === "polling" || phase === "manual_checking";

  return (
    <Layout>
      <div className="max-w-xl mx-auto px-5 py-20 text-center">
        {paid ? (
          <CheckCircle2 data-testid="payment-success-icon" className="w-16 h-16 text-[#4A6B56] mx-auto" />
        ) : timedOut ? (
          <RefreshCw data-testid="payment-timeout-icon" className="w-16 h-16 text-[#D67B54] mx-auto" />
        ) : (
          <Loader2 className="w-16 h-16 text-[#4A6B56] mx-auto animate-spin" />
        )}
        <h1 className="mt-4 text-3xl tracking-tight font-medium" style={{ fontFamily: "Outfit" }}>
          {paid ? "Payment confirmed" : timedOut ? "Still finalising with Stripe" : "Confirming payment…"}
        </h1>
        <p className="mt-2 text-[#6B6965]">
          {paid
            ? "We've notified the other party and updated your bookings. If you paid from the Binshare mobile app, you can now return to the app — your booking will refresh automatically."
            : timedOut
            ? "Stripe is taking longer than usual to confirm this payment. If you were charged, it typically clears within a minute or two — tap Check again below, or return to the app and refresh."
            : `Hold tight — checking with Stripe (attempt ${attempts + 1}).`}
        </p>
        {!paid && (
          <p data-testid="payment-return-hint" className="mt-3 text-sm text-[#8A8580]">
            Once you see <span className="font-medium text-[#1B3624]">&quot;Payment confirmed&quot;</span>, switch back to the Binshare app — your booking will refresh automatically.
          </p>
        )}

        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {timedOut && (
            <Button
              data-testid="payment-check-again"
              onClick={manualCheck}
              className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Check again
            </Button>
          )}
          {phase === "manual_checking" && (
            <Button disabled className="rounded-full bg-[#1B3624] text-white opacity-70">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…
            </Button>
          )}
          {(paid || timedOut || checking) && (
            <Button
              data-testid="return-to-app-btn"
              onClick={() => openBinshareApp(sessionId)}
              className={
                paid
                  ? "rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white"
                  : "rounded-full bg-white border border-[#1B3624] text-[#1B3624] hover:bg-[#1B3624]/5"
              }
            >
              <ExternalLink className="w-4 h-4 mr-2" /> Return to Binshare app
            </Button>
          )}
        </div>

        {paid && photoSaved && (
          <p data-testid="dropoff-saved" className="mt-6 text-sm text-[#4A6B56]">Photo saved — thanks for keeping the marketplace clean.</p>
        )}
        {paid && (
          <Card
            data-testid="payment-protection-badge"
            className="mt-8 rounded-2xl border-[#1B3624] bg-[#1B3624] text-white p-5 text-left"
          >
            <div className="flex gap-3 items-start">
              <CheckCircle2 className="w-5 h-5 text-[#D67B54] mt-0.5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#D67B54] font-semibold">£500 Flytipping Guarantee is active</div>
                <p className="mt-1 text-xs text-white/80">
                  Your carrier is licensed by the Environment Agency and the payment is held in escrow until you confirm the job.
                  If your waste is illegally disposed of, we'll cover your costs up to £500 — subject to our{" "}
                  <a href="/policies" className="underline hover:text-white">terms</a>.
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}
